import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { EVENT_FORMATS, type City, type EventFormat } from "@nifgash/shared/types";
import { listEvents, type EventCard } from "../api/events";
import { Button } from "../components/Button";
import { Chip } from "../components/Chip";
import { CitySelect } from "../components/CitySelect";
import { EventsMap } from "../components/EventsMap";
import { Loader } from "../components/Loader";
import { MascotPensive } from "../components/Mascot";
import { Sheet } from "../components/Sheet";
import { ThemeToggle } from "../components/ThemeToggle";
import { tg } from "../lib/telegram";
import { EventCardView } from "./EventCard";

type Filters = { city: City | null; formats: EventFormat[] };

export function Events() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [events, setEvents] = useState<EventCard[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isPremium, setIsPremium] = useState(false);
  const [filters, setFilters] = useState<Filters>({ city: null, formats: [] });
  const [filterOpen, setFilterOpen] = useState(false);
  const [mapVisible, setMapVisible] = useState(false);
  const [focusedId, setFocusedId] = useState<string | null>(null);

  const formatsKey = filters.formats.join(",");
  useEffect(() => {
    load(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.city, formatsKey]);

  function load(f: Filters) {
    setEvents(null);
    listEvents({ city: f.city ?? undefined, formats: f.formats })
      .then((res) => {
        setEvents(res.events);
        setIsPremium(res.is_premium);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }

  useEffect(() => {
    const app = tg();
    app?.MainButton.hide();
    app?.BackButton.hide();
  }, []);

  function updateOne(updated: EventCard) {
    setEvents((prev) => prev?.map((e) => (e.id === updated.id ? updated : e)) ?? prev);
  }

  const activeFilterCount = (filters.city ? 1 : 0) + filters.formats.length;

  // When the map is open and a pin is selected, hoist that event's card to
  // the top so its details are right there without scrolling. Clearing the
  // selection (tap empty map / another pin) restores the original order.
  const orderedEvents = useMemo(() => {
    if (!events) return events;
    if (!mapVisible || !focusedId) return events;
    const idx = events.findIndex((e) => e.id === focusedId);
    if (idx <= 0) return events;
    const copy = [...events];
    const [hit] = copy.splice(idx, 1);
    return [hit!, ...copy];
  }, [events, mapVisible, focusedId]);

  return (
    <>
      <div className="min-h-screen bg-ink-50 pb-32">
        {/* Header */}
        <div className="bg-surface border-b border-ink-100 px-5 pt-6 pb-5 sticky top-0 z-10">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <h1 className="text-[26px] font-extrabold text-ink-900 tracking-tight leading-tight">
                {t("events.upcoming")}
              </h1>
              <p className="text-[14px] text-ink-500 mt-1">
                {filters.city ? t(`labels.city.${filters.city}`) : t("events.allCities")}
                {filters.formats.length === 1
                  ? ` · ${t(`labels.format.${filters.formats[0]}`)}`
                  : filters.formats.length > 1
                    ? ` · ${filters.formats.length} ${t("events.formatsCountSuffix")}`
                    : ""}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setMapVisible((v) => !v)}
                aria-label={mapVisible ? t("events.toggleMapOff") : t("events.toggleMapOn")}
                aria-pressed={mapVisible}
                className={`w-10 h-10 rounded-full flex items-center justify-center border active:scale-90 transition-all ${
                  mapVisible
                    ? "bg-brand-purple border-brand-purple text-white shadow-[var(--shadow-card)]"
                    : "bg-ink-50 border-ink-100 text-ink-400"
                }`}
              >
                <MapIcon />
              </button>
              <button
                onClick={() => setFilterOpen(true)}
                aria-label={t("events.filtersAria")}
                className="relative w-10 h-10 rounded-full flex items-center justify-center bg-ink-50 border border-ink-100 text-ink-700 active:scale-90 transition-all"
              >
                <FilterIcon />
                {activeFilterCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-brand-purple text-white text-[10px] font-bold flex items-center justify-center">
                    {activeFilterCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => navigate("/create-event")}
                aria-label={t("events.createEventAria")}
                className="w-10 h-10 rounded-full flex items-center justify-center bg-brand-purple text-white shadow-[var(--shadow-card)] active:scale-90 transition-all"
              >
                <PlusIcon />
              </button>
              <ThemeToggle />
            </div>
          </div>
        </div>

        {/* Map — only when toggled on and there are events with coordinates. */}
        {mapVisible && events && events.length > 0 && (
          <div className="px-4 pt-3">
            <EventsMap
              events={events}
              height={300}
              focusedId={focusedId}
              onMarkerClick={(id) => setFocusedId(id)}
              onMapClick={() => setFocusedId(null)}
              onPopupCta={(id) => navigate(`/events/${id}`)}
            />
          </div>
        )}

        {/* Body */}
        <div className="pt-3 space-y-3 px-1">
          {error && (
            <div className="mx-4 mt-4 p-4 rounded-[var(--radius-md)] bg-brand-red-soft text-brand-red text-[14px] font-medium">
              {t("events.loadError", { error })}
            </div>
          )}

          {events == null && !error && <Loader text={t("events.loading")} />}

          {events && events.length === 0 && <EmptyState filtered={activeFilterCount > 0} />}

          {orderedEvents && orderedEvents.map((e) => (
            // When the map is open, tapping a card focuses its pin on the map.
            // The inner card buttons (RSVP/share) still work — their clicks
            // bubble up and harmlessly re-focus the same pin.
            <div
              key={e.id}
              onClick={mapVisible ? () => setFocusedId(e.id) : () => navigate(`/events/${e.id}`)}
              className={`cursor-pointer ${mapVisible && focusedId === e.id ? "ring-2 ring-brand-purple rounded-[var(--radius-lg)]" : ""}`}
            >
              <EventCardView event={e} onUpdate={updateOne} />
            </div>
          ))}
        </div>
      </div>

      <FilterSheet
        open={filterOpen}
        filters={filters}
        isPremium={isPremium}
        onClose={() => setFilterOpen(false)}
        onApply={(f) => {
          setFilters(f);
          setFilterOpen(false);
        }}
      />
    </>
  );
}

function FilterSheet({
  open,
  filters,
  isPremium,
  onClose,
  onApply,
}: {
  open: boolean;
  filters: Filters;
  isPremium: boolean;
  onClose: () => void;
  onApply: (f: Filters) => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [city, setCity] = useState<City | null>(filters.city);
  const [formats, setFormats] = useState<EventFormat[]>(filters.formats);

  useEffect(() => {
    if (open) {
      setCity(filters.city);
      setFormats(filters.formats);
    }
  }, [open, filters.city, filters.formats]);

  function toggleFormat(f: EventFormat) {
    setFormats((prev) => (prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f]));
  }

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={t("events.sheetTitle")}
      footer={
        <div className="grid grid-cols-2 gap-2">
          <Button
            size="lg"
            variant="secondary"
            onClick={() => {
              setCity(null);
              setFormats([]);
              onApply({ city: null, formats: [] });
            }}
          >
            {t("events.reset")}
          </Button>
          <Button size="lg" onClick={() => onApply({ city, formats: isPremium ? formats : [] })}>
            {t("events.apply")}
          </Button>
        </div>
      }
    >
      <div className="space-y-5">
        <div>
          <div className="text-[13px] font-semibold text-ink-500 uppercase tracking-wider mb-2">
            {t("events.filterCity")}
          </div>
          <CitySelect value={city} onChange={setCity} allowAll />
        </div>
        <div>
          <div className="text-[13px] font-semibold text-ink-500 uppercase tracking-wider mb-2">
            {t("events.filterFormat")}
          </div>
          {isPremium ? (
            <div className="flex flex-wrap gap-2">
              {/* Multi-select: tap several formats; empty = all. */}
              <Chip size="md" selected={formats.length === 0} onClick={() => setFormats([])}>
                {t("events.filterAll")}
              </Chip>
              {EVENT_FORMATS.map((f) => (
                <Chip key={f} size="md" selected={formats.includes(f)} onClick={() => toggleFormat(f)}>
                  {t(`labels.format.${f}`)}
                </Chip>
              ))}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => {
                onClose();
                navigate("/premium");
              }}
              className="w-full text-start rounded-[var(--radius-md)] border border-brand-purple/30 bg-brand-purple-soft p-4 flex items-center gap-3 active:scale-[0.99] transition-transform"
            >
              <span className="text-[18px]">🔒</span>
              <span className="flex-1">
                <span className="block text-[14px] font-semibold text-ink-900">
                  {t("events.filterFormatLocked")}
                </span>
                <span className="block text-[12px] text-ink-500 mt-0.5">
                  {t("events.filterFormatLockedCta")}
                </span>
              </span>
            </button>
          )}
        </div>
      </div>
    </Sheet>
  );
}

function PlusIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function FilterIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" />
    </svg>
  );
}

function MapIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 4 3 6v14l6-2 6 2 6-2V4l-6 2-6-2z" />
      <path d="M9 4v14M15 6v14" />
    </svg>
  );
}

function EmptyState({ filtered }: { filtered: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
      <MascotPensive size={140} />
      <h2 className="text-[20px] font-bold text-ink-900 mt-6">
        {filtered ? t("events.emptyFiltersTitle") : t("events.emptyTitle")}
      </h2>
      <p className="text-[14px] text-ink-500 mt-2 max-w-[280px] leading-relaxed">
        {filtered ? t("events.emptyFiltersText") : t("events.emptyText")}
      </p>
    </div>
  );
}
